/**
 * Staff detail setup card (DNKADM-0492 / DNKAPI-0714): one card that lists each item the
 * server reports missing, in plain words, each with a link to the place that fixes it.
 *
 *  - `NO_LOCATION`, `LOGIN_NOT_LINKED` → opens Edit details (assigned locations, Login).
 *  - `NO_HOURS` → scrolls to the Weekly schedule section on this page.
 *  - `NO_SERVICES` → the Services screen (a service's staff and locations, and publishing).
 *  - `CALENDAR_NOT_LINKED` → scrolls to the Calendar sync section on this page.
 *
 * The first three stop members from booking the coach. The last two are warnings and read
 * as warnings. Nothing renders when the field is absent or nothing is missing.
 */
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Icon } from '@/theme';
import type { StaffSetupHealth, StaffSetupItem } from '@/api/staff';
import { SETUP_ITEM_FIX, SETUP_ITEM_TEXT, isSetupWarning } from './setupHealth';

/** The in-page section ids the card scrolls to. */
export const STAFF_SCHEDULE_SECTION_ID = 'staff-weekly-schedule';
export const STAFF_CALENDAR_SECTION_ID = 'staff-calendar-sync';

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

export function StaffSetupCard({
  health,
  onEditDetails,
}: {
  health: StaffSetupHealth | undefined;
  /** Opens Edit details, where assigned locations and the Login are set. */
  onEditDetails: () => void;
}) {
  if (health === undefined || health.missing.length === 0) return null;
  const blockers = health.missing.filter((item) => !isSetupWarning(item));
  const warnings = health.missing.filter(isSetupWarning);

  function fix(item: StaffSetupItem) {
    const label = SETUP_ITEM_FIX[item];
    if (item === 'NO_SERVICES') {
      return (
        <MuiLink component={RouterLink} to="/services" sx={{ fontSize: 13.5 }}>
          {label}
        </MuiLink>
      );
    }
    const onClick =
      item === 'NO_HOURS'
        ? () => scrollToSection(STAFF_SCHEDULE_SECTION_ID)
        : item === 'CALENDAR_NOT_LINKED'
          ? () => scrollToSection(STAFF_CALENDAR_SECTION_ID)
          : onEditDetails;
    return (
      <MuiLink component="button" type="button" onClick={onClick} sx={{ fontSize: 13.5 }}>
        {label}
      </MuiLink>
    );
  }

  function row(item: StaffSetupItem) {
    const warning = isSetupWarning(item);
    return (
      <Box
        component="li"
        key={item}
        data-testid={`setup-item-${item}`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
      >
        <Icon
          name={warning ? 'warning' : 'error'}
          sx={{ fontSize: 18, mt: '2px', color: warning ? 'warning.main' : 'error.main' }}
        />
        <Box>
          <Typography variant="body2">
            {warning ? `Warning: ${SETUP_ITEM_TEXT[item]}` : SETUP_ITEM_TEXT[item]}
          </Typography>
          {fix(item)}
        </Box>
      </Box>
    );
  }

  return (
    <Alert
      severity={blockers.length > 0 ? 'error' : 'warning'}
      icon={false}
      sx={{ maxWidth: 620, mb: 3 }}
      data-testid="staff-setup-card"
    >
      <AlertTitle>
        {blockers.length > 0 ? 'Not bookable yet' : 'Bookable, with warnings'}
      </AlertTitle>
      {blockers.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Members can’t book this coach until these are fixed.
        </Typography>
      )}
      <Stack
        component="ul"
        aria-label="What this coach still needs"
        sx={{ listStyle: 'none', p: 0, m: 0, gap: 1 }}
      >
        {blockers.map(row)}
        {warnings.map(row)}
      </Stack>
    </Alert>
  );
}
